import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format } from 'date-fns';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Request interface for the new object-based API request
interface ApiRequestConfig {
  url: string;
  method: string;
  data?: unknown;
  headers?: Record<string, string>;
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

// Legacy version for backward compatibility
/* eslint-disable no-redeclare */
export async function apiRequest<T = any>(
  method: string,
  url: string,
  data?: unknown | undefined
): Promise<T>;

// New object-based version
export async function apiRequest<T = any>(config: ApiRequestConfig): Promise<T>;

// Implementation that handles both function signatures
export async function apiRequest<T = any>(
  methodOrConfig: string | ApiRequestConfig,
  url?: string,
  data?: unknown
): Promise<T> {
  /* eslint-enable no-redeclare */
  let finalUrl: string;
  let finalMethod: string;
  let finalData: unknown | undefined;
  let finalHeaders: Record<string, string> = {};

  // Determine which function signature was used
  if (typeof methodOrConfig === 'string') {
    // Legacy signature (method, url, data)
    finalMethod = methodOrConfig;
    finalUrl = url as string;
    finalData = data;
    if (finalData) {
      finalHeaders = { 'Content-Type': 'application/json' };
    }
  } else {
    // New object-based signature
    finalMethod = methodOrConfig.method;
    finalUrl = methodOrConfig.url;
    finalData = methodOrConfig.data;
    finalHeaders = methodOrConfig.headers || {};
    if (finalData && !finalHeaders['Content-Type']) {
      finalHeaders['Content-Type'] = 'application/json';
    }
  }

  const res = await fetch(finalUrl, {
    method: finalMethod,
    headers: finalHeaders,
    body: finalData ? JSON.stringify(finalData) : undefined,
    credentials: 'include',
  });

  await throwIfResNotOk(res);
  return res.json();
}

/**
 * Format a date into a readable string
 * @param date The date to format
 * @param dateFormat The format to use (defaults to 'dd MMM yyyy')
 * @returns Formatted date string or empty string if date is null/undefined
 */
export function formatDate(
  date: Date | string | null | undefined,
  dateFormat = 'dd MMM yyyy'
): string {
  if (!date) return '';
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return format(dateObj, dateFormat);
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
}
